import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "routing-and-pages"
    }}>{`Routing and pages`}</h1>
    <p>{`Routing is a core feature of modern JAMStack frameworks. What does it take to create a new page on a new URL?`}</p>
    <p>{`Both Gatsby and NextJS support filename-based routing. Create a new file and it becomes a page with its own URL.`}</p>
    <p>{`Where they differ is how you create dynamic pages.`}</p>
    <h2 {...{
      "id": "gatsby-filename-routing"
    }}>{`Gatsby filename routing`}</h2>
    <p>{`You can create Gatsby pages in many ways. Most commonly through an existing plugin that talks to a data source.`}</p>
    <p>{`By default, Gatsby comes with filename-based pages. Add a file to `}<inlineCode parentName="p">{`src/pages`}</inlineCode>{` and export a default React component. That's your page.`}</p>
    <h3 {...{
      "id": "exercise"
    }}>{`Exercise`}</h3>
    <p>{`Keep going in your original Gatsby repository, or switch to the `}<inlineCode parentName="p">{`exercise-2`}</inlineCode>{` folder in the exercises repo.`}</p>
    <p>{`Create a new page in `}<inlineCode parentName="p">{`src/pages`}</inlineCode>{`. Wrap your component in `}<inlineCode parentName="p">{`<Layout>`}</inlineCode>{`, try what happens if you use ThemeUI's `}<inlineCode parentName="p">{`<Container>`}</inlineCode>{` component instead. Don't forget to set page title with the `}<inlineCode parentName="p">{`<SEO>`}</inlineCode>{` component.`}</p>
    <p>{`Experiment with different `}<a parentName="p" {...{
        "href": "https://theme-ui.com/components"
      }}>{`components from ThemeUI`}</a>{`. This is your time to express yourself like it's 1994.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "800px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/b5597f64642ec0dad91660318c440bab/4b190/geocities.jpg",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "74.88789237668162%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'300\\'%20viewBox=\\'0%200%20400%20300\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M383%2023c-1%202%200%209%201%209v1l-2%204v3h18V22h-8l-9%201M15%2027l-1%205v4h101v5l-1%206c-1%200-2%202-2%207%200%206%200%207-2%209l-2%202v-4l-1-5-1%202%201%202-2-1h-3l2%202c2%201%203%202%202%203l1%202v2l1%201%201-1%201-2%201-2%201%201c2%207%203%206-41%207-38%200-41-1-41-2l2-2c2-1%204-6%203-9%200-2-2-3-2%200l-2%203c-2%201-2%201-2-2-1-2-1-2-1%202%200%202%200%203-1%202%200-1-1-2-2-1v3l-1-1h-4c-2%201-2%201%200%201l2%203c1%202%200%203-3%203l-5%201c-1%202%203%202%2050%202h52V54h3l2-1h-3c-3%200-4-1-4-2%200-2%200-2%201-1s1-3%201-11V26H66l-51%201m377%2018v4c0%202%200%202-3%202l-4%201-131%201a7390%207390%200%20006%201h138v60h-3c-3%201-3%201%200%201h4V52l-3%201c-4%200-4%200-3-3v-5h-1M37%2049v4c0%203%200%203%203%203%202%200%203-1%203-3s1-2%2013-2%2012%200%2012%202l2%202%201-3v-3H37M0%20164v113l1-112V54h6c7%200%206-1-1-2H0v112m129-76c0%208%200%208%202%208s3-1%203-4l1-5%201%204c0%205%200%205%202%205s2%200%202-4c0-5-2-8-5-7-2%201-2%200-2-2s0-3-2-3-2%201-2%208m-31%2022v43l5%202c5%200%207%202%2014%209%204%205%209%2013%209%2017l2%206%207%2034c1%209%200%2012-5%2012l-2%201a1001%201001%200%2000-2%2039l-1%203h169l-1-13c0-11-1-13-3-17l-4-11c-2-6-11-16-17-19l-12-6c-12-4-18-7-15-7%202%200%202%200%201-12v-46l-4-1c-3%200-4%200-4%202l-5%2026c-1%207-3%2010-4%2013l-4%207c0%203%200%203-2%201s-1-5%201-5c2-1%203-3%201-3v-1c2-4%200-23-3-22l-4-9c-4-10-6-13-13-17l-3-4c0-2-2-3-3-2%200%202-1%202-4%200h-4c-2%201-6-1-6-3-1-2%201-8%202-8h12c0%201%203%201%206-1%204-1%206-6%204-7s-34-2-35-1l-1%2011v11l-4%203a554%20554%200%20000%203c2-1%204-2%205-1l4-1h1l2%201v-1c-4-3%206-2%2015%201%206%201%206%202%206%2015l1%2010%204%201c4%202%204%202%204%207-1%206-1%207-3%205s-3%200-2%205%200%207-3%207l-2%201c-2%202-8%201-10-2-4-6-1-10%206-10%206%200%209-3%209-7%200-2-1-3-2-3l-1%201c0%201-2%202-5%202l-7%202%202%201%203%201h-2l-6%201c-3%202-3%202-3%207l1%205%201-1h1v3c2%202-1%207-3%207-3-1-3%201%200%202l1%202%201%202c0%201%201%201%201-1l3-2c2%200%203-1%203-4%200-2%201-3%205-4%207-2%209-1%206%202l-2%202c0%202%203%201%205-1%205-5%207%203%204%2012l-2%204c0-2%200-2-2%200l-2%201-3%203-6%204-5%203h-4c-2%200-2%200-1%201%201%202%2012%202%2013%200l2-1-2%203c-3%203-10%205-14%204-3-1-2%201%201%203%205%203%208%203%2016%200%2010-3%2010-3%202%201s-16%206-19%204c-3-1-4-6-3-8l-2-4c-2%200-3-2-3-3%200-2-2-2-8%202-8%204-13%2010-18%2021s-6%2011-8%208c-2-1-2-4-2-21a1151%201151%200%20017-120H98m155%2030c-2%201-3%2012-1%2049l1%2016%2013%201%2020%201%207%201v-11h-16c-13%200-15-1-17-2-2-2-1-4%204-6s12-2%2023%200h6v-50h-4c-8-2-33-1-36%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
        `}<source parentName="picture" {...{
              "srcSet": ["/static/b5597f64642ec0dad91660318c440bab/ca0a1/geocities.webp 223w", "/static/b5597f64642ec0dad91660318c440bab/75680/geocities.webp 445w", "/static/b5597f64642ec0dad91660318c440bab/d00b9/geocities.webp 800w"],
              "sizes": "(max-width: 800px) 100vw, 800px",
              "type": "image/webp"
            }}></source>{`
        `}<source parentName="picture" {...{
              "srcSet": ["/static/b5597f64642ec0dad91660318c440bab/853b2/geocities.jpg 223w", "/static/b5597f64642ec0dad91660318c440bab/0a4fe/geocities.jpg 445w", "/static/b5597f64642ec0dad91660318c440bab/4b190/geocities.jpg 800w"],
              "sizes": "(max-width: 800px) 100vw, 800px",
              "type": "image/jpeg"
            }}></source>{`
        `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/b5597f64642ec0dad91660318c440bab/4b190/geocities.jpg",
              "alt": "geocities",
              "title": "geocities",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
      `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`Link to your new page from the homepage with Gatsby's `}<inlineCode parentName="p">{`<Link>`}</inlineCode>{` component.`}</p>
    <h3 {...{
      "id": "solution"
    }}>{`Solution`}</h3>
    <p>{`Find mine in the `}<inlineCode parentName="p">{`exercise-2-solution`}</inlineCode>{` branch.`}</p>
    <h2 {...{
      "id": "nextjs-filename-routing"
    }}>{`NextJS filename routing`}</h2>
    <p>{`NextJS uses filename routing only. You create new pages by adding files to the `}<inlineCode parentName="p">{`pages/`}</inlineCode>{` directory. Create a file, export a default React component, and that's your page.`}</p>
    <h3 {...{
      "id": "exercise-1"
    }}>{`Exercise`}</h3>
    <p>{`Copy your page from the Gatsby exercise to your NextJS repository. You won't need `}<inlineCode parentName="p">{`<Layout>`}</inlineCode>{`, but you should add a `}<inlineCode parentName="p">{`<Head>`}</inlineCode>{` to give your page a title.`}</p>
    <p>{`Link to your new page from the homepage with NextJS's `}<inlineCode parentName="p">{`<Link>`}</inlineCode>{` component. Import from `}<inlineCode parentName="p">{`next/link`}</inlineCode>{`.`}</p>
    <p>{`Note that NextJS uses React 17 by default so you don't need to `}<inlineCode parentName="p">{`import React`}</inlineCode>{`.`}</p>
    <h3 {...{
      "id": "solution-1"
    }}>{`Solution`}</h3>
    <p>{`Find my final result in the `}<inlineCode parentName="p">{`exercise-2-solution`}</inlineCode>{` branch.`}</p>
    <h2 {...{
      "id": "gatsby-dynamic-pages"
    }}>{`Gatsby dynamic pages`}</h2>
    <p>{`Dynamic pages are Gatsby's biggest advantage `}<em parentName="p">{`and`}</em>{` its strongest drawback. The flexibility means you can create pages out of any data source ever. It's amazing.`}</p>
    <p>{`But `}<em parentName="p">{`you`}</em>{` have to create those pages. Or find a plugin. You almost have too much control.`}</p>
    <p>{`And remember, all pages have to be created in advance. During build time.`}</p>
    <h3 {...{
      "id": "exercise-2"
    }}>{`Exercise`}</h3>
    <p>{`Grab your solution from before and create pages based on hardcoded data.`}</p>
    <p>{`Export an async `}<inlineCode parentName="p">{`createPages`}</inlineCode>{` function from `}<inlineCode parentName="p">{`gatsby-node.js`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`exports.createPages = async ({ actions }) => {}
`}</code></pre>
    <p>{`Use the `}<inlineCode parentName="p">{`createPage`}</inlineCode>{` action to create your pages. Each needs a `}<inlineCode parentName="p">{`path`}</inlineCode>{`, a page `}<inlineCode parentName="p">{`component`}</inlineCode>{`, and you can pass custom data as `}<inlineCode parentName="p">{`context`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`context`}</inlineCode>{` populates the `}<inlineCode parentName="p">{`pageContext`}</inlineCode>{` prop of your page component.`}</p>
    <p>{`Something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// gatsby-node.js

const path = require("path")

exports.createPages = async ({ actions }) => {
  for (let number of [1, 2, 3]) {
    actions.createPage({
      path: \`dynamic-page-\${number}\`,
      // you'll need to create a template
      component: path.resolve("src/templates/dynamic-page.js"),
      context: {
        pageNumber: number,
      },
    })
  }
}
`}</code></pre>
    <p>{`Access your page at `}<inlineCode parentName="p">{`http://localhost:8000/dynamic-page-1`}</inlineCode>{`. Try linking to the others.`}</p>
    <h3 {...{
      "id": "solution-2"
    }}>{`Solution`}</h3>
    <p>{`Find my final result in the `}<inlineCode parentName="p">{`exercise-2-solution`}</inlineCode>{` branch.`}</p>
    <h2 {...{
      "id": "nextjs-dynamic-pages"
    }}>{`NextJS dynamic pages`}</h2>
    <p>{`NextJS dynamic pages come in two flavors:`}</p>
    <ul>
      <li parentName="ul">{`statically generated at build time`}</li>
      <li parentName="ul">{`server-side rendered on each request`}</li>
    </ul>
    <p>{`And you define both the same way 👉 with `}<a parentName="p" {...{
        "href": "https://nextjs.org/docs/routing/dynamic-routes"
      }}>{`dynamic file-based routing`}</a>{`.`}</p>
    <p>{`Whether they become a statically generated, or server-side rendered page depends on what you put in the file. We'll explore that further in exercises with dynamic data.`}</p>
    <h3 {...{
      "id": "exercise-3"
    }}>{`Exercise`}</h3>
    <p>{`Create a `}<inlineCode parentName="p">{`pages/dynamic-page/[number].js`}</inlineCode>{` file in your NextJS project from before. You can adapt your dynamic page template from Gatsby.`}</p>
    <p>{`The part in square brackets becomes the dynamic portion of your URL. You can access the value from the NextJS router.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const DynamicPage = () => {
    const router = useRouter()
    const { number } = router.query
`}</code></pre>
    <p>{`Notice that because pages are created on-demand, you aren't limited to the `}<inlineCode parentName="p">{`[1,2,3]`}</inlineCode>{` static data like the Gatsby example.`}</p>
    <h3 {...{
      "id": "solution-3"
    }}>{`Solution`}</h3>
    <p>{`Find my final result in the `}<inlineCode parentName="p">{`exercise-2-solution`}</inlineCode>{` branch.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      